/* eslint-disable @atlaskit/design-system/no-css-tagged-template-expression -- needs manual remediation */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { Component } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import type { IntlShape, WithIntlProps, WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';

import ButtonGroup from '@atlaskit/button/button-group';
import type { DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics/types/dispatch-analytics-event';
import Announcer from '@atlaskit/editor-common/announcer';
import type { ExtensionProvider } from '@atlaskit/editor-common/extensions/extension-provider';
import type { Item } from '@atlaskit/editor-common/floating-toolbar';
import { areSameItems } from '@atlaskit/editor-common/floating-toolbar';
import messages from '@atlaskit/editor-common/floating-toolbar/messages';
import Button from '@atlaskit/editor-common/FloatingToolbar/Button';
import Separator from '@atlaskit/editor-common/FloatingToolbar/Separator';
import commonMessages from '@atlaskit/editor-common/messages';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { areToolbarFlagsEnabled } from '@atlaskit/editor-common/toolbar-flag-check';
import type { SelectOption } from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { backgroundPaletteTooltipMessages } from '@atlaskit/editor-common/ui-color/ColorPalette/paletteMessagesTokenModeNames';
import type { PaletteColor } from '@atlaskit/editor-common/ui-color/ColorPalette/type';
import ColorPickerButton from '@atlaskit/editor-common/ui-menu/ColorPickerButton';
import { ToolbarArrowKeyNavigationProvider } from '@atlaskit/editor-common/ui-menu/ToolbarArrowKeyNavigationProvider';
import { hexToEditorBackgroundPaletteColor } from '@atlaskit/editor-palette/background';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { token } from '@atlaskit/tokens';

import type { FloatingToolbarPlugin } from '../floatingToolbarPluginType';
import { checkShouldForceFocusAndApply, forceFocusSelector } from '../pm-plugins/force-focus';
import { showConfirmDialog } from '../pm-plugins/toolbar-data/commands';
import Dropdown from './Dropdown';
import { EmojiPickerButton } from './EmojiPickerButton';
import { ExtensionsPlaceholder } from './ExtensionsPlaceholder';
import { Input } from './Input';
import { ScrollButton } from './ScrollButton';
import { ScrollButtons } from './ScrollButtons';
import Select from './Select';

export interface Props {
	api: ExtractInjectionAPI<FloatingToolbarPlugin> | undefined;
	className?: string;
	/** See `FloatingToolbarConfig.containerSurface`. Defaults to `'default'`. */
	containerSurface?: 'default' | 'none';
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent;
	dispatchCommand: (command?: Function) => void;
	editorView?: EditorView;
	extensionsProvider?: Promise<ExtensionProvider>;
	focusEditor?: () => void;
	groupLabel?: string;
	items: Array<Item>;
	mediaAssistiveMessage?: string;
	node: Node;
	popupsBoundariesElement?: HTMLElement;
	popupsMountPoint?: HTMLElement;
	popupsScrollableElement?: HTMLElement;
	providerFactory?: ProviderFactory;
	scrollable?: boolean;
	shouldFitContainer?: boolean;
	target?: HTMLElement;
}

type GroupedItems = (Item | Item[])[];

// eslint-disable-next-line jsdoc/require-jsdoc
export function groupItems(items: Item[], areAnyNewToolbarFlagsEnabled: boolean): GroupedItems {
	const groupItems = items.reduce(
		(
			accumulator: {
				buttonGroup: Item[];
				finalOutput: GroupedItems;
			},
			item,
			i,
		) => {
			const { finalOutput, buttonGroup } = accumulator;

			if (item.type === 'button') {
				const notLastItem = i < items.length - 1;
				const nextItemIsButton = items[i + 1] && items[i + 1].type === 'button';
				const wasPreviousButton = items[i - 1] && items[i - 1].type === 'button';

				const shouldBeRadioButton = (notLastItem && nextItemIsButton) || wasPreviousButton;

				// Only group as radio button if not explicitly set to false
				const isRadioButton = shouldBeRadioButton && item.isRadioButton !== false;

				if (isRadioButton) {
					item.isRadioButton = true;
					buttonGroup.push(item);

					if (!nextItemIsButton && wasPreviousButton) {
						finalOutput.push(buttonGroup);
						accumulator.buttonGroup = [];
					}
				} else {
					finalOutput.push(item);
				}
			} else if (item.type === 'separator' && areAnyNewToolbarFlagsEnabled) {
				const isLeadingSeparator = i === 0;
				const isTrailingSeparator = i === items.length - 1;
				const isDuplicateSeparator = items[i - 1]?.type === 'separator';

				!isLeadingSeparator &&
					!isTrailingSeparator &&
					!isDuplicateSeparator &&
					finalOutput.push(item);
			} else {
				finalOutput.push(item);
			}

			return accumulator;
		},
		{
			buttonGroup: [],
			finalOutput: [],
		},
	);

	return groupItems.finalOutput;
}

const ToolbarItems = React.memo(
	({
		items,
		groupLabel,
		dispatchCommand,
		popupsMountPoint,
		popupsBoundariesElement,
		editorView,
		dispatchAnalyticsEvent,
		popupsScrollableElement,
		scrollable,
		providerFactory,
		extensionsProvider,
		node,
		setDisableScroll,
		mountRef,
		api,
		intl,
	}: Props & {
		intl: IntlShape;
		mounted: boolean;
		mountRef: React.RefObject<HTMLDivElement>;
		setDisableScroll?: (disable: boolean) => void;
	}) => {
		const emojiAndColourPickerMountPoint = scrollable
			? popupsMountPoint ||
				editorView?.dom.closest('.fabric-editor-popup-scroll-parent') ||
				editorView?.dom.closest('.ak-editor-content-area') ||
				undefined
			: popupsMountPoint;

		const areAnyNewToolbarFlagsEnabled = areToolbarFlagsEnabled(Boolean(api?.toolbar));

		const renderItem = (item: Item, idx: number) => {
			switch (item.type) {
				case 'button':
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const ButtonIcon = item.icon as React.ComponentClass<any>;

					const onClickHandler = () => {
						if (item.confirmDialog) {
							dispatchCommand(showConfirmDialog(idx));
						} else {
							dispatchCommand(item.onClick);
							if (item.focusEditoronEnter && !editorView?.hasFocus()) {
								editorView?.focus();
							}
						}
					};

					const getIconColor = (disabled?: boolean, selected?: boolean) => {
						if (disabled) {
							return token('color.icon.disabled');
						}
						if (selected) {
							return token('color.icon.selected');
						}
						return 'currentColor';
					};

					return (
						<Button
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
							className={item.className}
							key={idx}
							title={item.title}
							href={item.href}
							icon={
								item.icon ? (
									<ButtonIcon
										color={getIconColor(item.disabled, item.selected)}
										spacing="spacious"
										label={undefined}
										aria-hidden={true} // Icon is described by the button for screen readers
									/>
								) : undefined
							}
							iconAfter={item.iconAfter ? <item.iconAfter label="" /> : undefined}
							appearance={item.appearance}
							target={item.target}
							onClick={onClickHandler}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onMouseEnter={() => dispatchCommand(item.onMouseEnter)}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onMouseLeave={() => dispatchCommand(item.onMouseLeave)}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onFocus={() => dispatchCommand(item.onFocus)}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onBlur={() => dispatchCommand(item.onBlur)}
							onMount={item.onMount}
							onUnmount={item.onUnmount}
							selected={item.selected}
							disabled={item.disabled}
							tooltipContent={item.tooltipContent}
							testId={item.testId}
							hideTooltipOnClick={item.hideTooltipOnClick}
							hasNewContentOnTriggerClick={item.hasNewContentOnTriggerClick}
							ariaHasPopup={item.ariaHasPopup}
							tabIndex={item.tabIndex}
							isRadioButton={item.isRadioButton}
							ariaLabel={item?.ariaLabel}
							pulse={item.pulse}
							interactionName={item.interactionName}
							areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
						>
							{item.showTitle && item.title}
						</Button>
					);

				case 'input':
					return (
						<Input
							key={idx}
							mountPoint={popupsMountPoint}
							boundariesElement={popupsBoundariesElement}
							defaultValue={item.defaultValue}
							placeholder={item.placeholder}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onSubmit={(value) => dispatchCommand(item.onSubmit(value))}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onBlur={(value) => dispatchCommand(item.onBlur(value))}
						/>
					);

				case 'custom': {
					return item.render(editorView, idx, dispatchAnalyticsEvent, {
						popupsBoundariesElement,
						popupsMountPoint,
						popupsScrollableElement,
						scrollable,
						setDisableParentScroll: scrollable ? setDisableScroll : undefined,
					});
				}

				case 'overflow-dropdown':
					// if an option has a confirmDialog, we need to replace its onClick handler
					// to set the state to show the confirm dialog

					// crudely done here to avoid greater coupling with DropdownMenuItem from `floating-toolbar`
					// which would need knowledge of indexes, showConfirmDialog etc.
					const options = item.options.map((option, optionIndex) => {
						if (!('type' in option) && option.confirmDialog) {
							const onClick = option.confirmDialog
								? showConfirmDialog(idx, optionIndex)
								: option.onClick;

							return { ...option, onClick };
						}

						return option;
					});

					return (
						<Dropdown
							alignX={areAnyNewToolbarFlagsEnabled ? 'right' : undefined}
							key={idx}
							title={intl.formatMessage(commonMessages.viewMore)}
							icon={<ShowMoreHorizontalIcon label="" spacing="spacious" />}
							dispatchCommand={dispatchCommand}
							options={options}
							disabled={item.disabled}
							tooltip={item.tooltip}
							hideExpandIcon
							mountPoint={popupsMountPoint}
							boundariesElement={popupsBoundariesElement}
							scrollableElement={popupsScrollableElement}
							dropdownWidth={item.dropdownWidth}
							showSelected={item.showSelected}
							buttonTestId={item.testId}
							editorView={editorView}
							setDisableParentScroll={scrollable ? setDisableScroll : undefined}
							dropdownListId={item?.id && `${item.id}-dropdownList`}
							alignDropdownWithToolbar={items.length === 1}
							onClick={item.onClick}
							areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
						/>
					);

				case 'dropdown':
					const DropdownIcon = item.icon;
					const BeforeIcon = item.iconBefore;
					return (
						<Dropdown
							key={idx}
							title={item.title}
							icon={DropdownIcon && <DropdownIcon label={item.title} />}
							iconBefore={BeforeIcon && <BeforeIcon label="" />}
							dispatchCommand={dispatchCommand}
							options={item.options}
							disabled={item.disabled}
							tooltip={item.tooltip}
							hideExpandIcon={item.hideExpandIcon}
							mountPoint={popupsMountPoint}
							boundariesElement={popupsBoundariesElement}
							scrollableElement={popupsScrollableElement}
							dropdownWidth={item.dropdownWidth}
							showSelected={item.showSelected}
							buttonTestId={item.testId}
							editorView={editorView}
							setDisableParentScroll={scrollable ? setDisableScroll : undefined}
							dropdownListId={item?.id && `${item.id}-dropdownList`}
							alignDropdownWithToolbar={items.length === 1}
							onToggle={item.onToggle}
							footer={item.footer}
							onMount={item.onMount}
							onClick={item.onClick}
							pulse={item.pulse}
							shouldFitContainer={item.shouldFitContainer}
							areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
						/>
					);

				case 'select':
					if (item.selectType === 'list') {
						const ariaLabel = item.title || item.placeholder;
						return (
							<Select
								key={idx}
								dispatchCommand={dispatchCommand}
								options={item.options}
								hideExpandIcon={item.hideExpandIcon}
								// Ignored via go/ees005
								// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
								mountPoint={scrollable ? mountRef.current! : undefined}
								boundariesElement={popupsBoundariesElement}
								scrollableElement={popupsScrollableElement}
								defaultValue={item.defaultValue}
								placeholder={item.placeholder}
								// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								onChange={(selected) => dispatchCommand(item.onChange(selected as SelectOption))}
								ariaLabel={ariaLabel}
								filterOption={item.filterOption}
								setDisableParentScroll={scrollable ? setDisableScroll : undefined}
								classNamePrefix={'floating-toolbar-select'}
							/>
						);
					}
					if (item.selectType === 'color') {
						return (
							<ColorPickerButton
								skipFocusButtonAfterPick
								key={idx}
								isAriaExpanded={item.isAriaExpanded}
								hideExpandIcon={item.hideExpandIcon}
								title={item.title}
								// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								onChange={(selected) => {
									dispatchCommand(item.onChange(selected));
								}}
								cols={item.cols}
								colorPalette={item.options as PaletteColor[]}
								currentColor={item.defaultValue ? item.defaultValue.value : undefined}
								placement="Panels"
								mountPoint={emojiAndColourPickerMountPoint}
								setDisableParentScroll={scrollable ? setDisableScroll : undefined}
								hexToPaletteColor={
									isExperimentEnabled('platform_editor_lovability_dividers')
										? (item.hexToPaletteColor ?? hexToEditorBackgroundPaletteColor)
										: hexToEditorBackgroundPaletteColor
								}
								paletteColorTooltipMessages={
									isExperimentEnabled('platform_editor_lovability_dividers')
										? (item.paletteColorTooltipMessages ?? backgroundPaletteTooltipMessages)
										: backgroundPaletteTooltipMessages
								}
								returnEscToButton={item.returnEscToButton}
							/>
						);
					}
					if (item.selectType === 'emoji') {
						return (
							<EmojiPickerButton
								key={idx}
								editorView={editorView}
								title={item.title}
								providerFactory={providerFactory}
								isSelected={item.selected}
								// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								onChange={(selected) => dispatchCommand(item.onChange(selected))}
								mountPoint={emojiAndColourPickerMountPoint}
								popupsBoundariesElement={popupsBoundariesElement}
								setDisableParentScroll={scrollable ? setDisableScroll : undefined}
								pluginInjectionApi={api}
							/>
						);
					}
					return null;

				case 'extensions-placeholder':
					if (!editorView || !extensionsProvider) {
						return null;
					}

					return (
						<ExtensionsPlaceholder
							key={idx}
							node={node}
							editorView={editorView}
							extensionProvider={extensionsProvider}
							separator={item.separator}
							applyChangeToContextPanel={api?.contextPanel?.actions.applyChange}
							extensionApi={api?.extension?.actions.api()}
							dispatchCommand={dispatchCommand}
							popupsMountPoint={popupsMountPoint}
							popupsBoundariesElement={popupsBoundariesElement}
							popupsScrollableElement={popupsScrollableElement}
							alignDropdownWithToolbar={items.length === 1}
							scrollable={scrollable}
							areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
						/>
					);
				case 'separator':
					if (areAnyNewToolbarFlagsEnabled) {
						return item.fullHeight ? (
							<Separator key={idx} fullHeight={true} areAnyNewToolbarFlagsEnabled={true} />
						) : null;
					}
					return (
						<Separator
							key={idx}
							fullHeight={item.fullHeight}
							areAnyNewToolbarFlagsEnabled={false}
						/>
					);
			}
		};

		const groupedItems = groupItems(
			// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
			items.filter((item) => !item.hidden),
			areAnyNewToolbarFlagsEnabled,
		);

		return (
			<ButtonGroup testId="editor-floating-toolbar-items">
				{groupedItems.map((element, index) => {
					const isGroup = Array.isArray(element);

					if (isGroup) {
						return (
							<div
								// Ignored via go/ees005
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								css={areAnyNewToolbarFlagsEnabled ? buttonGroupStylesNew : buttonGroupStyles}
								role="radiogroup"
								aria-label={groupLabel ?? undefined}
								data-testid="editor-floating-toolbar-grouped-buttons"
							>
								{element.map((element) => {
									const indexInAllItems = items.findIndex((item) => item === element);
									return renderItem(element, indexInAllItems);
								})}
							</div>
						);
					} else {
						const indexInAllItems = items.findIndex((item) => item === element);
						return renderItem(element, indexInAllItems);
					}
				})}
			</ButtonGroup>
		);
	},
	(prevProps, nextProps) => {
		if (!nextProps.node) {
			return false;
		}
		// only rerender toolbar items if the node is different
		// otherwise it causes an issue where multiple popups stays open
		return !(
			prevProps.node.type !== nextProps.node.type ||
			prevProps.node.attrs.localId !== nextProps.node.attrs.localId ||
			prevProps.popupsBoundariesElement !== nextProps.popupsBoundariesElement ||
			prevProps.popupsMountPoint !== nextProps.popupsMountPoint ||
			prevProps.popupsScrollableElement !== nextProps.popupsScrollableElement ||
			prevProps.scrollable !== nextProps.scrollable ||
			prevProps.setDisableScroll !== nextProps.setDisableScroll ||
			!areSameItems(prevProps.items, nextProps.items) ||
			!prevProps.mounted !== !nextProps.mounted
		);
	},
);

const buttonGroupStyles = css({
	display: 'flex',
	gap: token('space.050'),
});

const buttonGroupStylesNew = css({
	display: 'flex',
	gap: token('space.075'),
});

const toolbarContainerBase = css({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div > div': {
		alignItems: 'center',
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const toolbarContainer = (
	areAnyNewToolbarFlagsEnabled: boolean,
	scrollable?: boolean,
	hasSelect?: boolean,
	firstElementIsSelect?: boolean,
	// See `FloatingToolbarConfig.containerSurface`: 'none' is for a single custom item that
	// already renders its own complete surface, so this wrapper's own surface is omitted.
	containerSurface: 'default' | 'none' = 'default',
) =>
	css(
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		containerSurface === 'none'
			? undefined
			: {
					backgroundColor: token('elevation.surface.overlay'),
					borderRadius: token('radius.small', '3px'),
					boxShadow: token('elevation.shadow.overlay'),
				},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		containerSurface === 'none'
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({ padding: token('space.0') })
			: scrollable
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					css(
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
						hasSelect
							? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								css({
									height: '40px',
								})
							: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								css({
									height: '32px',
								}),
						{
							overflow: 'hidden',
						},
					)
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
					areAnyNewToolbarFlagsEnabled
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
						css(
							{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
								padding: `${token('space.0')} 4px ${token('space.0')} 4px`,
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							firstElementIsSelect &&
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								css({
									paddingLeft: token('space.050'),
								}),
						)
					: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css(
							{
								padding: `${token('space.050')} ${token('space.100')}`,
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							firstElementIsSelect &&
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								css({
									paddingLeft: token('space.050'),
								}),
						),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		areAnyNewToolbarFlagsEnabled
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				css({ minHeight: token('space.500') })
			: undefined,
	);

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const toolbarOverflow = ({
	scrollable,
	scrollDisabled,
	firstElementIsSelect,
	areAnyNewToolbarFlagsEnabled,
}: {
	areAnyNewToolbarFlagsEnabled: boolean;
	firstElementIsSelect?: boolean;
	scrollable?: boolean;
	scrollDisabled?: boolean;
}) =>
	css(
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		scrollable
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css(
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					scrollDisabled
						? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							css({
								overflow: 'hidden',
							})
						: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							css({
								overflowX: 'auto',
								overflowY: 'hidden',
								// When scrollable is true, ScrollButtons will be shown, hence we want to hide show default horizontal scrollbar
								scrollbarWidth: 'none',
							}),
					{
						WebkitOverflowScrolling: 'touch',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
						padding: `${token('space.050')} 0 ${token('space.050')}`,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
						'> div': {
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							'> div:first-child': firstElementIsSelect
								? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									css({
										marginLeft: token('space.050'),
									})
								: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									css({
										marginLeft: token('space.100'),
									}),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
							'> div:nth-last-child(1 of :not(:where([popover], dialog)))': {
								marginRight: token('space.100'),
							},
						},
					},
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
					areAnyNewToolbarFlagsEnabled
						? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							css({
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
								padding: `${token('space.0')} 4px ${token('space.600')} 4px`,
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
								'> div': {
									minHeight: token('space.500'),
									gap: token('space.075'),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
									'> div:first-child': {
										marginLeft: 0,
									},
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
									'> div:nth-last-child(1 of :not(:where([popover], dialog)))': {
										marginRight: 0,
									},
								},
							})
						: undefined,
				)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({ display: 'flex' }),
	);

export interface State {
	mounted: boolean;
	scrollDisabled: boolean;
}

// eslint-disable-next-line @repo/internal/react/no-class-components
class Toolbar extends Component<Props & WrappedComponentProps, State> {
	private scrollContainerRef: React.RefObject<HTMLDivElement>;
	private mountRef: React.RefObject<HTMLDivElement>;
	private toolbarContainerRef: React.RefObject<HTMLDivElement>;

	constructor(props: Props & WrappedComponentProps) {
		super(props);
		this.scrollContainerRef = React.createRef<HTMLDivElement>();
		this.mountRef = React.createRef<HTMLDivElement>();
		this.toolbarContainerRef = React.createRef<HTMLDivElement>();
		this.state = {
			scrollDisabled: false,
			mounted: false,
		};
	}

	// remove any decorations added by toolbar buttons i.e danger and selected styling
	// this prevents https://product-fabric.atlassian.net/browse/ED-10207
	private resetStyling() {
		if (this.props.editorView) {
			const { state, dispatch } = this.props.editorView;
			this.props.api?.decorations?.actions.removeDecoration(state, dispatch);
		}
	}

	private setDisableScroll = (disabled: boolean) => {
		// wait before setting disabled state incase users jumping from one popup to another
		if (disabled) {
			requestAnimationFrame(() => {
				this.setState({ scrollDisabled: disabled });
			});
		} else {
			this.setState({ scrollDisabled: disabled });
		}
	};

	componentDidMount() {
		this.setState({ mounted: true });
	}

	componentDidUpdate(prevProps: Props) {
		checkShouldForceFocusAndApply(this.props?.editorView);

		if (this.props.node !== prevProps.node) {
			this.resetStyling();
		}
	}

	componentWillUnmount() {
		const { editorView } = this.props;
		if (editorView) {
			const {
				state: { tr },
				dispatch,
			} = editorView;
			dispatch(forceFocusSelector(null)(tr));
		}

		this.resetStyling();
	}

	private shouldHandleArrowKeys = (): boolean => {
		//To prevent the keydown handling of arrow keys for custom toolbar items with 'disableArrowNavigation' prop enabled,
		//Usually the button which has menus or popups
		return !this.props.items?.find((item) => item.type === 'custom' && item.disableArrowNavigation);
	};

	private handleEscape = (event: KeyboardEvent): void => {
		// If any menu is open inside the floating toolbar 'Esc' key should not
		// focus the editorview.
		// Event can't be stopped as they are not childnodes of floating toolbar

		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		const isDropdownOpen = !!document.querySelector('[data-role="droplistContent"]');
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		const isSelectMenuOpen = !!document.querySelector('.floating-toolbar-select__menu');

		if (isDropdownOpen || isSelectMenuOpen) {
			return;
		}

		this.props.editorView?.focus();
		event.preventDefault();
		event.stopPropagation();
	};

	private captureMouseEvent = (event: React.MouseEvent) => {
		// Don't capture mouse event for custom toolbars e.g. insert hyperlink
		if (this.props.items?.length === 1 && this.props.items[0].type === 'custom') {
			return;
		}

		// Prevents toolbar from closing when clicking on the toolbar itself and not on the buttons
		event.stopPropagation();
		event.preventDefault();
	};

	private isShortcutToFocusToolbar = (event: KeyboardEvent) => {
		//Alt + F10 to reach first element in this floating toolbar
		return event.altKey && (event.key === 'F10' || event.keyCode === 121);
	};

	render() {
		const { items, className, node, intl, scrollable, mediaAssistiveMessage } = this.props;
		const areAnyNewToolbarFlagsEnabled = areToolbarFlagsEnabled(Boolean(this.props.api?.toolbar));

		if (!items || !items.length) {
			return null;
		}

		// Select has left padding of 4px to the border, everything else 8px
		const firstElementIsSelect = items[0].type === 'select';
		const hasSelect = items.find((item) => item.type === 'select' && item.selectType === 'list');

		return (
			<React.Fragment>
				<ToolbarArrowKeyNavigationProvider
					editorView={this.props.editorView}
					handleEscape={this.handleEscape}
					disableArrowKeyNavigation={!this.shouldHandleArrowKeys()}
					childComponentSelector={"[data-testid='editor-floating-toolbar']"}
					isShortcutToFocusToolbar={this.isShortcutToFocusToolbar}
					intl={intl}
				>
					<div
						ref={this.toolbarContainerRef}
						css={() => [
							toolbarContainerBase,
							toolbarContainer(
								areAnyNewToolbarFlagsEnabled,
								scrollable,
								hasSelect !== undefined,
								firstElementIsSelect,
								this.props.containerSurface,
							),
						]}
						aria-label={intl.formatMessage(messages.floatingToolbarAriaLabel)}
						role="toolbar"
						data-testid="editor-floating-toolbar"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={className}
						onMouseDown={areAnyNewToolbarFlagsEnabled ? this.captureMouseEvent : undefined}
					>
						<Announcer
							text={
								mediaAssistiveMessage
									? `${mediaAssistiveMessage}, ${intl.formatMessage(
											messages.floatingToolbarAnnouncer,
										)}`
									: intl.formatMessage(messages.floatingToolbarAnnouncer)
							}
							delay={250}
						/>
						{scrollable && areAnyNewToolbarFlagsEnabled && (
							<ScrollButton
								intl={intl}
								scrollContainerRef={this.scrollContainerRef}
								node={node}
								disabled={this.state.scrollDisabled}
								side="left"
							/>
						)}
						<div
							data-testid="floating-toolbar-items"
							ref={this.scrollContainerRef}
							// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
							css={toolbarOverflow({
								areAnyNewToolbarFlagsEnabled,
								scrollable,
								scrollDisabled: this.state.scrollDisabled,
								firstElementIsSelect,
							})}
						>
							<ToolbarItems
								// Ignored via go/ees005
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...this.props}
								setDisableScroll={this.setDisableScroll}
								mountRef={this.mountRef}
								mounted={this.state.mounted}
							/>
						</div>
						{scrollable &&
							(areAnyNewToolbarFlagsEnabled ? (
								<ScrollButton
									intl={intl}
									scrollContainerRef={this.scrollContainerRef}
									node={node}
									disabled={this.state.scrollDisabled}
									side="right"
								/>
							) : (
								<ScrollButtons
									intl={intl}
									scrollContainerRef={this.scrollContainerRef}
									node={node}
									disabled={this.state.scrollDisabled}
									areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
								/>
							))}
					</div>
					<div ref={this.mountRef}></div>
				</ToolbarArrowKeyNavigationProvider>
			</React.Fragment>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/no-restricted-types
const _default_1: React.FC<WithIntlProps<Props & WrappedComponentProps>> & {
	WrappedComponent: React.ComponentType<Props & WrappedComponentProps>;
} = injectIntl(Toolbar);
export default _default_1;
